// стили темы
import "../scss/style.scss";

// Lazy bridge for classic component scripts; private playback has a single adapter.
window.CeoMedia = Object.freeze({
  createClient: async config => (await import('./pages/media-client')).createMediaClient(config),
  mountPlayer: async (container, options) => (await import('./pages/media-player')).mountMediaPlayer(container, options),
});
window.dispatchEvent(new Event('ceo-media-ready'));

// 1) jQuery — в window
import $ from "jquery";
window.$ = $;
window.jQuery = $;

// остальное
import * as bootstrap from "bootstrap";
window.bootstrap = bootstrap;

const registerBootstrapJqueryBridge = () => {
  if (!window.jQuery || !window.jQuery.fn || !window.bootstrap) {
    return;
  }

  const $jq = window.jQuery;
  const bridge = (pluginName, Constructor) => {
    if (!Constructor || typeof Constructor.getOrCreateInstance !== "function") {
      return;
    }

    $jq.fn[pluginName] = function (configOrMethod, ...args) {
      return this.each(function () {
        const config = typeof configOrMethod === "object" ? configOrMethod : undefined;
        const instance = Constructor.getOrCreateInstance(this, config);

        if (typeof configOrMethod === "string" && typeof instance[configOrMethod] === "function") {
          instance[configOrMethod](...args);
        }
      });
    };
  };

  bridge("tooltip", window.bootstrap.Tooltip);
  bridge("popover", window.bootstrap.Popover);
  bridge("modal", window.bootstrap.Modal);
  bridge("dropdown", window.bootstrap.Dropdown);
  bridge("tab", window.bootstrap.Tab);
  bridge("collapse", window.bootstrap.Collapse);
};

registerBootstrapJqueryBridge();

import moment from "moment";
import "moment/locale/ru";
import "bootstrap-daterangepicker";
import "bootstrap-daterangepicker/daterangepicker.css";

import feather from "feather-icons";
window.feather = feather;

// SmartWizard — jQuery-плагин
import "smartwizard/dist/css/smart_wizard_all.min.css";
import "smartwizard/dist/js/jquery.smartWizard.min.js";
window.smartWizard = $.fn.smartWizard;

// ваш код
import "./core/functions.js";
import "./core/deferred-media.js";
import "./core/main.js";
import "./core/color-scheme.js";
import "./pages/learning.js";
import "./pages/learning-ai.js";
import "./pwa/index.js";
import { initSchedule } from "./pages/schedule";
import { initServiceRequestsPage } from "./pages/service-requests";
import { initStoreProductForm } from "./pages/store-product-form";

const earlyPageKey = document.body?.dataset?.page || document.querySelector("[data-page]")?.dataset?.page || "";
if (earlyPageKey === "marketing-ranks") {
  window.__MARKETING_RANKS_EXTERNAL__ = true;
}

const injectGlobalModalScrollStyles = () => {
  if (typeof document === "undefined") {
    return;
  }

  if (document.getElementById("global-modal-scroll-style")) {
    return;
  }

  const styleEl = document.createElement("style");
  styleEl.id = "global-modal-scroll-style";
  styleEl.textContent = `
    .modal { overflow-y: auto; }
    .modal .modal-dialog {
      max-height: calc(100dvh - 1rem);
      margin-top: 0.5rem;
      margin-bottom: 0.5rem;
    }
    .modal .modal-content {
      max-height: inherit;
      display: flex;
      flex-direction: column;
      overflow-x: hidden;
      overflow-y: auto;
    }
    .modal .modal-body {
      min-height: 0;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      scrollbar-gutter: stable;
    }
    .modal .modal-body::-webkit-scrollbar { width: 8px; }
  `;

  if (document.head) {
    document.head.appendChild(styleEl);
  }
};

window.moment = window.moment || moment;
if (window.moment && typeof window.moment.locale === "function") {
  window.moment.locale("ru");
}

if (typeof window.getDaterangepickerLocale !== "function") {
  window.getDaterangepickerLocale = () => {
    const localeData = window.moment ? window.moment.localeData() : null;
    return {
      format: "DD/MM/YYYY",
      separator: " - ",
      applyLabel: "Применить",
      cancelLabel: "Отмена",
      fromLabel: "С",
      toLabel: "По",
      customRangeLabel: "Произвольный",
      weekLabel: "Нд",
      daysOfWeek: localeData ? localeData.weekdaysMin() : ["Вс", "Пн", "Вт", "Ср", "Чт", "Пт", "Сб"],
      monthNames: localeData ? localeData.months() : [],
      firstDay: localeData ? localeData.firstDayOfWeek() : 1,
    };
  };
}

const initBaseDatepickers = () => {
  ["datepicker", "inlinedatepicker", "daterange", "daterangeranges"].forEach((fnName) => {
    if (typeof window[fnName] === "function") {
      try {
        window[fnName]();
      } catch (error) {
        console.error(error);
      }
    }
  });
};

let editorModulePromise = null;
const loadEditorModule = () => {
  if (!editorModulePromise) {
    editorModulePromise = import(/* webpackChunkName: "editor" */ "./pages/editor")
      .then((m) => {
        if (typeof m.initEditorPage === "function") {
          m.initEditorPage();
        }
        return m;
      });
  }
  return editorModulePromise;
};

window.ensureDaterangepicker = () => {
  return loadEditorModule().then((m) => (typeof m.ensureDaterangepicker === "function" ? m.ensureDaterangepicker() : true));
};

window.ensureSummernote = () => {
  return loadEditorModule().then((m) => (typeof m.ensureSummernote === "function" ? m.ensureSummernote() : true));
};
window.ensureRichTextEditor = () => {
  return loadEditorModule().then((m) => (typeof m.ensureRichTextEditor === "function" ? m.ensureRichTextEditor() : true));
};
window.setRichTextEditorValue = (textarea, value) => {
  return loadEditorModule().then((m) => {
    if (typeof m.setRichTextEditorValue === "function") {
      m.setRichTextEditorValue(textarea, value);
    } else if (textarea instanceof HTMLTextAreaElement) {
      textarea.value = String(value ?? "");
    }
  });
};

const editorSelectors =
  "textarea[data-rich-editor], textarea.ceo-rich-editor, textarea.summernote, textarea.summernote-inited, .schedule-date, .schedule-time, #sr_date, [data-editor-init], #datepicker, #daterangepicker, #daterangepickerranges, #inlinewrap1";
const richTextEditorSelectors = "textarea[data-rich-editor], textarea.ceo-rich-editor, textarea.summernote, textarea.summernote-inited";

const bootstrapEditorModules = () => {
  if (!document.querySelector(editorSelectors)) {
    return Promise.resolve(false);
  }

  return loadEditorModule().then((m) => {
    if (document.querySelector(richTextEditorSelectors) && typeof m.ensureRichTextEditor === "function") {
      return m.ensureRichTextEditor().then(() => {
        if (typeof m.initRichTextEditors === "function") {
          m.initRichTextEditors();
        } else if (typeof window.initRichTextEditors === "function") {
          window.initRichTextEditors();
        }
        return true;
      });
    }
    return true;
  });
};

const isBackForwardNavigation = () => {
  if (typeof window === "undefined" || !window.performance || typeof window.performance.getEntriesByType !== "function") {
    return false;
  }
  const entries = window.performance.getEntriesByType("navigation");
  const entry = Array.isArray(entries) && entries.length > 0 ? entries[0] : null;
  return !!(entry && entry.type === "back_forward");
};

const initClipboardButtons = () => {
  document.addEventListener("click", (event) => {
    const button = event.target && event.target.closest ? event.target.closest("[data-copy-text]") : null;
    if (!button) {
      return;
    }
    const text = String(button.getAttribute("data-copy-text") || "").trim();
    if (!text) {
      return;
    }
    if (navigator.clipboard && typeof navigator.clipboard.writeText === "function") {
      navigator.clipboard.writeText(text).then(() => {
        const previous = button.innerHTML;
        button.innerHTML = '<i class="bi bi-check2 me-1"></i> Скопировано';
        window.setTimeout(() => {
          button.innerHTML = previous;
        }, 1400);
      }).catch(() => {});
    }
  });
};

const initRefPreviewRedirect = () => {
  const preview = document.querySelector("[data-ref-preview-redirect]");
  if (!preview) {
    return;
  }
  const target = String(preview.getAttribute("data-ref-preview-target") || "").trim();
  if (!target) {
    return;
  }
  window.setTimeout(() => {
    window.location.replace(target);
  }, 200);
};

window.addEventListener("pageshow", (event) => {
  if (!(event && event.persisted) && !isBackForwardNavigation()) {
    return;
  }
  bootstrapEditorModules().catch(console.error);
});

// === Ленивый импорт кроппера ТОЛЬКО на странице настроек компании ===
// document.addEventListener('DOMContentLoaded', () => {
//   // 1) вариант по маркеру-модалке
//   const onCompanySettings = document.getElementById('cropperModal');

//   // 2) (альтернатива) по атрибуту <body data-page="company-settings">
//   // const onCompanySettings = document.body?.dataset?.page === 'company-settings';

//   if (onCompanySettings) {
//     import(/* webpackChunkName: "company-settings" */ './pages/company-settings')
//       .then(m => m.initCompanySettingsCropper())
//       .catch(console.error);
//   }
// });

document.addEventListener('DOMContentLoaded', () => {
  injectGlobalModalScrollStyles();
  initClipboardButtons();
  initRefPreviewRedirect();

  const page = document.body?.dataset?.page || document.querySelector('[data-page]')?.dataset?.page;
  const pageName = typeof page === "string" ? page.toLowerCase() : "";

  initBaseDatepickers();

  // Cropper for company settings and platform-agent profile.
  if (page === 'company-settings' || document.querySelector('[data-company-cropper], [data-platform-agent-cropper]')) {
    import(/* webpackChunkName: "company-settings" */ './pages/company-settings')
      .then(m => m.initCompanySettingsCropper())
      .catch(console.error);
  }

  // Только там, где есть кнопки для медиакроппера (товар/услуга и т.п.)
  if (document.querySelector('[data-universal-cropper], .cropper-trigger')) {
    import(/* webpackChunkName: "universal-cropper" */ './pages/universal-cropper')
      .then(m => m.initUniversalCropper())
      .catch(console.error);
  }

  if (page === 'builder-editor') {
    import(/* webpackChunkName: "builder" */ './builder')
      .then(m => m.initBuilder())
      .catch(console.error);
  }

  if (page === "schedule") {
    try {
      initSchedule();
    } catch (error) {
      console.error(error);
    }
  }

  try {
    initServiceRequestsPage();
  } catch (error) {
    console.error(error);
  }

  if (
    page === 'marketing-marketing' ||
    page === 'marketing-monitoring' ||
    page === 'marketing-activity' ||
    page === 'marketing-structure' ||
    page === 'marketing-qualification' ||
    page === 'marketing-promotions' ||
    page === 'marketing-cashback' ||
    page === 'marketing-ranks' ||
    page === 'marketing-workers'
  ) {
    import(/* webpackChunkName: "marketing" */ './pages/marketing')
      .then(m => m.initMarketingPage())
      .catch(console.error);
  }

  if (page === "marketing-visual-flow" || page === "marketing-visualflow" || page === "marketing-visual_flow") {
    import(/* webpackChunkName: "marketing-visual-flow" */ "./pages/marketing-visual-flow")
      .then((m) => m.initMarketingVisualFlowPage())
      .catch(console.error);
  }

  if (document.querySelector("[data-ai-lab-app], [data-ai-creator-hub]")) {
    import(/* webpackChunkName: "ai-material-flow" */ "./pages/ai-material-flow")
      .then((m) => m.initAiMaterialFlow())
      .catch(console.error);
  }

  if (page === "platformmarketing-partners") {
    import(/* webpackChunkName: "platform-marketing-partners" */ "./pages/platform-marketing-partners")
      .then((m) => m.initPlatformMarketingPartnersPage())
      .catch(console.error);
  }

  if (page === "platformmarketing" || page === "platformmarketing-index") {
    import(/* webpackChunkName: "platform-marketing-admin" */ "./pages/platform-marketing-admin")
      .then((m) => m.initPlatformMarketingAdminPage())
      .catch(console.error);
  }

  if (page === "platformmarketing-simulator") {
    import(/* webpackChunkName: "platform-marketing-simulator" */ "./pages/platform-marketing-simulator")
      .then((m) => m.initPlatformMarketingSimulatorPage())
      .catch(console.error);
  }

  if (page === "dbmonitor" || page === "dbmonitor-company") {
    import(/* webpackChunkName: "db-monitor" */ "./pages/db-monitor")
      .then((m) => m.initDbMonitorPage())
      .catch(console.error);
  }

  if (page === "products-all") {
    import(/* webpackChunkName: "store-products-all" */ "./pages/store-products-all")
      .then((m) => m.initStoreProductsAllPage())
      .catch(console.error);
  }

  if (document.getElementById("productForm") && document.getElementById("short_desc")) {
    try {
      initStoreProductForm();
    } catch (error) {
      console.error(error);
    }
  }

  if (document.querySelector("[data-company-timezone-settings]")) {
    import(/* webpackChunkName: "company-timezone-settings" */ "./pages/company-timezone-settings")
      .then((m) => m.initCompanyTimezoneSettings())
      .catch(console.error);
  }

  if (page === "boards-view" || document.getElementById("boards-board-page")) {
    import(/* webpackChunkName: "boards" */ "./pages/boards")
      .then((m) => m.initBoardsPage())
      .catch(console.error);
  }

  if (page === "mail" || document.querySelector("[data-mail-app]")) {
    import(/* webpackChunkName: "mail" */ "./pages/mail")
      .then((m) => m.initMailPage())
      .catch(console.error);
  }

  if (document.getElementById("aiCenterRoot")) {
    import(/* webpackChunkName: "ai-center" */ "./pages/ai-center")
      .then((m) => m.initAiCenterPage())
      .catch(console.error);
  }

  if (document.getElementById("partner-ai-form") || document.querySelector("[data-partner-ai-page]")) {
    import(/* webpackChunkName: "platform-agent-ai" */ "./pages/platform-agent-ai")
      .then((m) => m.initPlatformAgentAiPage())
      .catch(console.error);
  }

  if (document.getElementById("aiCreatorHub")) {
    import(/* webpackChunkName: "ai-creator-hub" */ "./pages/ai-creator-hub")
      .then((m) => m.initAiCreatorHub())
      .catch(console.error);
  }

  if (document.getElementById("ceoAiAssistant")) {
    import(/* webpackChunkName: "ai-assistant-panel" */ "./components/ai-assistant-panel")
      .then((m) => m.initAiAssistantPanel())
      .catch(console.error);
  }

  if (document.getElementById("ai-worker-monitor")) {
    import(/* webpackChunkName: "admin-ai" */ "./pages/admin-ai")
      .then((m) => m.initAdminAiPage())
      .catch(console.error);
  }

  if (page === "agents-permissions" || document.getElementById("agent-permissions-page")) {
    import(/* webpackChunkName: "agents-permissions" */ "./pages/agents-permissions")
      .then((m) => m.initAgentPermissionsPage())
      .catch(console.error);
  }

  if (document.querySelector("#settings-tabs-ajax-root") && document.querySelector(".settings-top-tabs-link")) {
    import(/* webpackChunkName: "settings-tabs" */ "./pages/settings-tabs")
      .then((m) => m.initSettingsTabsPage())
      .catch(console.error);
  }

  if (page === 'crm' || pageName.startsWith('crm-') || document.querySelector("[data-crm-kanban-shell]")) {
    const params = new URLSearchParams(window.location.search || '');
    const section = (params.get('section') || '').toLowerCase();
    if (section === 'reports' || pageName === 'crm-reports') {
      import(/* webpackChunkName: "crm-reports" */ './pages/crm-reports')
        .then(m => m.initCrmReportsPage())
        .catch(console.error);
    } else {
      import(/* webpackChunkName: "crm-kanban" */ './pages/crm-kanban')
        .then(m => m.initCrmKanbanPage())
        .catch(console.error);
    }
  }

  const isAgentsPage = pageName === 'agents' || pageName === 'agents-structure';
  const pathName = (window.location && window.location.pathname ? window.location.pathname : '').toLowerCase();
  const isPlatformAgentsPage = pathName === '/partner/agents';
  const isWalletPage =
    pageName === 'main-wallet' ||
    pageName === 'wallet' ||
    pageName === 'main-wallet-agent' ||
    pageName === 'wallet-agent' ||
    pageName === 'main-wallet-company' ||
    pageName === 'wallet-company' ||
    pathName === '/main/wallet' ||
    pathName === '/main/wallet-agent' ||
    pathName === '/main/wallet-company';

  if (page === 'main-lk' || page === 'lk' || isAgentsPage || isPlatformAgentsPage || isWalletPage) {
    import(/* webpackChunkName: "lk" */ './pages/lk')
      .then(m => m.initLkPage())
      .catch(console.error);
  }

  bootstrapEditorModules().catch(console.error);

  import(/* webpackChunkName: "features" */ './core/features')
    .then(m => m.initCommonFeatures())
    .catch(console.error);
});

injectGlobalModalScrollStyles();
